/**
 * The test-day timetable, as the browser has to think about it.
 *
 * A day is where the centre is, not where the server is. "Saturday morning"
 * means nine o'clock in the room, and the browser is the only part of this
 * system that knows what that instant is — so the day and the hour are worked
 * out here and the instant is what travels.
 */

export const SLOTS = ['MORNING', 'AFTERNOON', 'EVENING'] as const;
export type Slot = (typeof SLOTS)[number];

export const SLOT_NAMES: Record<Slot, string> = {
  MORNING: 'Morning',
  AFTERNOON: 'Afternoon',
  EVENING: 'Evening',
};

/** When each slot starts unless the centre moves it. Local hours. */
export const SLOT_HOURS: Record<Slot, number> = {
  MORNING: 9,
  AFTERNOON: 14,
  EVENING: 18,
};

export const SESSION_LENGTH_MS = 4.5 * 60 * 60 * 1000;

export interface SessionPaper {
  id: string;
  title: string;
  module: string;
  status: string;
  setName: string | null;
}

export interface SessionCandidate {
  id: string;
  fullName: string;
  candidateNumber: string | null;
  groupName: string | null;
  sitting: string | null;
}

export interface SessionView {
  id: string;
  day: string;
  slot: Slot;
  name: string | null;
  startsAt: string;
  endsAt: string;
  shuffle: boolean;
  closedAt: string | null;
  state: 'PLANNED' | 'RUNNING' | 'CLOSED';
  papers: SessionPaper[];
  candidates: SessionCandidate[];
  tests: number;
  canShuffle: boolean;
}

export interface SessionResultRow {
  student: { id: string; fullName: string; candidateNumber: string | null };
  listening: number | null;
  reading: number | null;
  writing: number | null;
  average: number | null;
  sitting: string | null;
  sat: boolean;
  awaitingWriting: boolean;
}

export interface SessionReport {
  session: SessionView;
  rows: SessionResultRow[];
  summary: {
    seated: number;
    sat: number;
    absent: number;
    awaitingWriting: number;
    average: number | null;
    highest: number | null;
    lowest: number | null;
  };
}

/**
 * A day as the timetable writes it, in local time.
 *
 * Not `toISOString().slice(0, 10)`, which is the UTC day: east of Greenwich
 * that turns an evening session into the following morning's date, and a
 * centre in Tashkent would find its Saturday evening filed under Sunday.
 */
export function dayKey(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const dayOfMonth = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${dayOfMonth}`;
}

/** The Monday of the week a date falls in, at midnight, local. */
export function startOfWeek(date: Date): Date {
  const out = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  // getDay() is 0 on Sunday, which belongs to the week that began six days ago.
  const back = (out.getDay() + 6) % 7;
  out.setDate(out.getDate() - back);
  return out;
}

/** `count` consecutive days from `from`, at local midnight. */
export function daysFrom(from: Date, count: number): Date[] {
  return Array.from({ length: count }, (_, i) => {
    const day = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    day.setDate(day.getDate() + i);
    return day;
  });
}

/** When a slot on a given day begins, as an instant. */
export function startsAtFor(day: string, slot: Slot): Date {
  const [year, month, dayOfMonth] = day.split('-').map(Number);
  return new Date(year!, month! - 1, dayOfMonth!, SLOT_HOURS[slot], 0, 0, 0);
}

/** Half past two, not 14:30:00.000Z. */
/**
 * Whether a slot's session would already be over before it was scheduled.
 *
 * A session lasts four and a half hours from its start, and a session whose
 * end has passed is closed — which is a state that refuses every edit. So
 * scheduling into a slot that is already over produces a session born closed:
 * no papers can be put in it, no candidates seated, nothing named. It was
 * offered on every past day of the week the timetable shows, and a teacher
 * clicking one got a dead card they could not even remove.
 */
export function slotHasPassed(day: string, slot: Slot, now: Date = new Date()): boolean {
  return startsAtFor(day, slot).getTime() + SESSION_LENGTH_MS <= now.getTime();
}

/**
 * A time of day as the centre reads one: 15:32, on the twenty-four-hour clock.
 *
 * Fixed rather than left to the browser, which read it out as "03:32 PM" on a
 * machine set to American English — in a country that does not use the
 * twelve-hour clock, beside dates written the British way.
 */
export const clockTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });

/** How long a running session has left, in whole minutes. */
export const minutesLeft = (endsAt: string, now: Date = new Date()): number =>
  Math.max(0, Math.floor((new Date(endsAt).getTime() - now.getTime()) / 60_000));

/** "1h 45m left", or "closing" once there is nothing left to round. */
export function timeLeft(endsAt: string, now: Date = new Date()): string {
  const minutes = minutesLeft(endsAt, now);
  if (minutes <= 0) return 'closing';
  const hours = Math.floor(minutes / 60);
  return hours > 0 ? `${hours}h ${minutes % 60}m left` : `${minutes}m left`;
}

const dateOf = (day: string): Date => {
  const [year, month, dayOfMonth] = day.split('-').map(Number);
  return new Date(year!, month! - 1, dayOfMonth!);
};

/**
 * What a day is called: today, tomorrow, or its weekday.
 *
 * A timetable is mostly read about this week, and "Thursday" is how a person
 * says a day this week. The date is its own line — see dayDate — rather than
 * the same day said twice, once in words and once as 2026-10-01.
 */
export function dayLabel(day: string, now: Date = new Date()): string {
  const today = dayKey(now);
  if (day === today) return 'Today';
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  if (day === dayKey(tomorrow)) return 'Tomorrow';
  return dateOf(day).toLocaleDateString('en-GB', { weekday: 'long' });
}

/**
 * The date under a day's name: "1 Oct".
 *
 * British order and spelling whatever the browser speaks, like every other
 * date in the console — the browser's own language had it reading "Sep 28"
 * on one line and "2026-09-28" on the next.
 */
export function dayDate(day: string): string {
  return dateOf(day).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}
